import React, { MouseEventHandler, useEffect, useRef } from 'react';

// Components
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { useGlobalMenu, GlobalMenuState } from '../../../contexts/GlobalMenuStore';
import { getBreakpointThreshold, getMenuIdentifierById } from './HeaderCollapsible.utils';
import { Tooltip, TooltipPlacement } from '../../../components/atoms/Tooltip';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { ButtonProps } from '../../../types/HtmlProps';

type HeaderCollapsibleProps = ButtonProps & {
  id: string;
  title: string;
  modifier: string;
  icon: string;
};

/**
 * Renders a button that opens and closes content.
 * e.g. the menu on the GenericPage.
 */
export function HeaderCollapsible({ icon, id, modifier, onClick, ...buttonProps }: HeaderCollapsibleProps) {
  const { windowInnerWidth } = useAppContext();
  const { currentOpenMenu, toggleMenu } = useGlobalMenu();
  const buttonRef = useRef(null);
  const isOpen = currentOpenMenu === getMenuIdentifierById(id);

  const toggleMenuHandler: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    const menu = getMenuIdentifierById(id);
    if (menu !== GlobalMenuState.NONE) {
      toggleMenu(menu);
    }
  };

  /* Close menu when breakpoint changes to avoid wrong state in App and DOM (falsely ste class modifier) */
  useEffect(() => {
    const menu = getMenuIdentifierById(id);
    if (
      id !== GlobalDocumentIds.personalBox &&
      windowInnerWidth >= getBreakpointThreshold(id) &&
      currentOpenMenu === menu
    ) {
      if (menu !== GlobalMenuState.NONE) {
        toggleMenu(menu);
      }
    }
  }, [windowInnerWidth, currentOpenMenu, toggleMenu, id]);

  return (
    <Tooltip
      placement={id === GlobalDocumentIds.mainNavigationBox ? TooltipPlacement.RIGHT : TooltipPlacement.LEFT}
      hideTooltipFromScreenReader
      hideOnClick
      attachTo={buttonRef}
    >
      <button
        ref={buttonRef}
        onClick={onClick || toggleMenuHandler}
        aria-controls={id}
        aria-expanded={isOpen}
        className={`header-collapsible header-collapsible--${modifier}`}
        type="button"
        {...buttonProps}
      >
        <span className="invisible">{buttonProps.title}</span>
        <SVG
          inverted
          svgClass={`header-collapsible__icon${isOpen ? '' : ' svg--clearbutton-js'}`}
          svgSpriteId={isOpen ? Icons.DECLINE_CROSS : icon}
        />
      </button>
    </Tooltip>
  );
}
